import { createMemo, createResource, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import type { VaultItemRecord } from "@boveda/powersync";
import {
  buildSecuritySignals,
  computeSecurityScore,
  crypto,
  getVaultItemsQuery,
  type ImprovementKind,
  queryKeys,
  type ScorableItem,
  type SecurityLevel,
  type SecurityScore,
} from "@boveda/shared";
import { createPowerSyncQuery } from "@boveda/shared/solid";
import { AlertTriangle, Copy, ShieldCheck, Sparkles } from "@fleet/icons";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useI18n } from "@/providers/i18n-provider";
import { useVault } from "../vault-context";
import { summarizeHealth } from "../vault-state";

/** Security health view: weak / reused passwords. */
export function VaultHealth() {
  const { t } = useI18n();
  const vault = useVault();
  const { db } = usePowerSyncContext();

  const itemsQuery = createPowerSyncQuery<VaultItemRecord>({
    queryKey: () => queryKeys.vault.health(vault.vaultId() ?? undefined),
    queryFn: () => {
      const drizzle = vault.drizzle();
      const vid = vault.vaultId();
      return drizzle && vid ? getVaultItemsQuery(drizzle, vid) : null;
    },
    db,
    enabled: () => Boolean(vault.vaultId()),
  });

  const [analysis] = createResource(
    () => itemsQuery.data ?? [],
    async (rows) => {
      const entries: { id: string; password: string; updatedAt?: string }[] = [];
      const scorable: ScorableItem[] = [];
      for (const row of rows) {
        try {
          const item = await vault.decryptRow(row);
          const pw = item.plain.fields.password;
          if (pw) {
            entries.push({ id: item.id, password: pw, updatedAt: item.plain.updatedAt });
          }
          scorable.push({
            id: item.id,
            type: item.plain.type,
            password: pw,
            totpSecret: item.plain.totpSecret,
            updatedAt: item.plain.updatedAt,
          });
        } catch {
          // item ilegible → se omite
        }
      }
      const report = crypto.analyzeVaultHealth(entries);
      // Security score: 100% local (zero-knowledge). Signals derived
      // WITHOUT secrets and scored; none of this leaves device.
      const signals = buildSecuritySignals(scorable, crypto.estimateStrength);
      return {
        summary: summarizeHealth(report, entries.length),
        score: computeSecurityScore(signals),
      };
    },
  );

  const s = createMemo(() => analysis()?.summary);
  const score = createMemo(() => analysis()?.score);

  return (
    <div class="mx-auto w-full max-w-2xl px-4 py-6">
      <h1 class="mb-1 text-xl font-semibold">{t(msg`Security check`)}</h1>
      <p class="mb-6 text-sm text-muted-foreground">
        {t(msg`Analyzed on this device — passwords never leave your vault.`)}
      </p>

      <Show when={score()}>{(sc) => <SecurityScoreCard score={sc()} />}</Show>

      <Show when={s()} fallback={<p class="text-sm text-muted-foreground">{t(msg`Analyzing…`)}</p>}>
        {(sum) => (
          <>
            <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <HealthCard
                tone="ok"
                icon={ShieldCheck}
                count={sum().healthy}
                label={t(msg`Healthy`)}
              />
              <HealthCard
                tone="warn"
                icon={AlertTriangle}
                count={sum().weak}
                label={t(msg`Weak`)}
                testid="health-weak"
              />
              <HealthCard
                tone="warn"
                icon={Copy}
                count={sum().reused}
                label={t(msg`Reused`)}
                testid="health-reused"
              />
            </div>
            <p class="mt-4 text-xs text-muted-foreground">
              {sum().total} {t(msg`passwords analyzed`)}
            </p>
          </>
        )}
      </Show>
    </div>
  );
}

const LEVEL_TONES: Record<SecurityLevel, string> = {
  "getting-started": "text-warning",
  building: "text-warning",
  solid: "text-primary",
  strong: "text-success",
  fortified: "text-success",
};

/**
 * Security score card (NON-punitive gamification). Shows global
 * score 0–100, achieved level and positive improvement suggestions. Everything
 * LOCALLY (zero-knowledge): passwords never leave vault.
 */
function SecurityScoreCard(props: { score: SecurityScore }) {
  const { t } = useI18n();

  const levelLabel = (): string => {
    switch (props.score.level) {
      case "fortified": {
        return t(msg`Fortified`);
      }
      case "strong": {
        return t(msg`Strong`);
      }
      case "solid": {
        return t(msg`Solid`);
      }
      case "building": {
        return t(msg`Building up`);
      }
      default: {
        return t(msg`Getting started`);
      }
    }
  };

  const improvementLabel = (kind: ImprovementKind, count: number): string => {
    switch (kind) {
      case "strengthen": {
        return t(msg`Strengthen ${count} password(s)`);
      }
      case "unique": {
        return t(msg`Make ${count} password(s) unique`);
      }
      case "refresh": {
        return t(msg`Refresh ${count} old password(s)`);
      }
      default: {
        return t(msg`Turn on 2FA for ${count} login(s)`);
      }
    }
  };

  return (
    <div
      class="mb-6 rounded-2xl border border-border/60 bg-card/40 p-5"
      data-testid="security-score"
    >
      <div class="flex items-center gap-4">
        <div class="flex flex-col items-center">
          <span
            class={`text-4xl font-bold tabular-nums ${LEVEL_TONES[props.score.level]}`}
            data-testid="security-score-value"
          >
            {props.score.score}
          </span>
          <span class="text-[0.6875rem] text-muted-foreground">/ 100</span>
        </div>
        <div class="min-w-0 flex-1">
          <div class="flex items-center gap-1.5">
            <Sparkles class={`h-4 w-4 ${LEVEL_TONES[props.score.level]}`} aria-hidden="true" />
            <span class="text-sm font-semibold" data-testid="security-score-level">
              {levelLabel()}
            </span>
          </div>
          <div class="mt-2 h-2 overflow-hidden rounded-full bg-muted">
            <div
              class="h-full rounded-full bg-primary transition-[width]"
              style={{ width: `${props.score.score}%` }}
            />
          </div>
        </div>
      </div>

      <Show
        when={props.score.improvements.length > 0}
        fallback={
          <p class="mt-4 text-xs text-success">
            {t(msg`Your vault hygiene looks great. Keep it up!`)}
          </p>
        }
      >
        <ul class="mt-4 flex flex-col gap-1.5" data-testid="security-score-tips">
          <For each={props.score.improvements}>
            {(imp) => (
              <li class="flex items-center gap-2 text-xs text-muted-foreground">
                <span class="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                {improvementLabel(imp.kind, imp.count)}
              </li>
            )}
          </For>
        </ul>
      </Show>
    </div>
  );
}

function HealthCard(props: {
  tone: "ok" | "warn";
  icon: typeof ShieldCheck;
  count: number;
  label: string;
  testid?: string;
}) {
  return (
    <div
      class="flex flex-col gap-1 rounded-xl border border-border/60 bg-card/40 p-4"
      data-testid={props.testid}
    >
      <props.icon
        class="h-5 w-5"
        classList={{
          "text-success": props.tone === "ok",
          "text-warning": props.tone === "warn" && props.count > 0,
          "text-muted-foreground": props.tone === "warn" && props.count === 0,
        }}
      />
      <span
        class="text-2xl font-semibold"
        data-testid={props.testid ? `${props.testid}-count` : undefined}
      >
        {props.count}
      </span>
      <span class="text-xs text-muted-foreground">{props.label}</span>
    </div>
  );
}
